import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { 
  X, 
  Users, 
  Clock, 
  DollarSign, 
  Trophy, 
  AlertCircle,
  CheckCircle,
  UserPlus,
  UserMinus,
  Link,
  Info,
  Play,
  Pause,
  Flag,
  Bell
} from 'lucide-react';
import { 
  GroupCompetition, 
  GroupParticipant, 
  WithdrawalReasonType,
  GroupNotification 
} from '../../types/group-competition.types';

interface GroupCompetitionLobbyProps {
  group: GroupCompetition;
  currentUserId: string;
  onClose: () => void;
  onJoinCompetition: (groupId: string) => void;
  onWithdraw: (groupId: string, reason: WithdrawalReasonType, customReason?: string) => void;
}

// Animations
const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const countdown = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #334155 100%);
  z-index: 1000;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  min-height: 90vh;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 15px;
`;

const TitleIcon = styled.span`
  font-size: 2.5rem;
  animation: ${pulse} 2s ease-in-out infinite;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 12px 20px;
  border-radius: 25px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  font-size: 14px;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
`;

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 30px;
  animation: ${slideIn} 0.5s ease-out;
`;

const StatusBanner = styled.div<{ status: string }>`
  background: ${props => {
    if (props.status === 'ACTIVELY_FORMING') return 'linear-gradient(45deg, #4caf50, #8bc34a)';
    if (props.status === 'IN_PROGRESS') return 'linear-gradient(45deg, #ff9800, #ffc107)';
    return 'linear-gradient(45deg, #9c27b0, #e91e63)';
  }};
  color: white;
  padding: 20px;
  border-radius: 15px;
  text-align: center;
  font-size: 1.2rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
`;

const StatusIcon = styled.div`
  font-size: 2rem;
  animation: ${countdown} 2s ease-in-out infinite;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const InfoCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  padding: 20px;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.08);
  }
`;

const InfoTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  color: #ffd700;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const InfoValue = styled.div`
  font-size: 1.3rem;
  font-weight: 700;
  color: white;
  margin-bottom: 5px;
`;

const InfoSubtext = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
`;

const CountdownTimer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border: 2px solid #ffd700;
  border-radius: 15px;
  padding: 20px;
  text-align: center;
  margin-bottom: 30px;
`;

const CountdownTitle = styled.h3`
  font-size: 1.2rem;
  color: #ffd700;
  margin-bottom: 15px;
`;

const CountdownDisplay = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-bottom: 15px;
`;

const TimeUnit = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
`;

const TimeValue = styled.div`
  background: rgba(255, 215, 0, 0.2);
  border: 1px solid #ffd700;
  border-radius: 10px;
  padding: 15px 20px;
  font-size: 2rem;
  font-weight: 700;
  color: #ffd700;
  min-width: 80px;
`;

const TimeLabel = styled.div`
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.7);
  text-transform: uppercase;
  font-weight: 600;
`;

const ParticipantsSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 25px;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #ffd700;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ParticipantsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 15px;
`;

const ParticipantCard = styled.div<{ isCurrentUser: boolean; status: string }>`
  background: ${props => props.isCurrentUser 
    ? 'rgba(255, 215, 0, 0.1)' 
    : 'rgba(255, 255, 255, 0.05)'
  };
  border: 2px solid ${props => {
    if (props.isCurrentUser) return '#ffd700';
    if (props.status === 'ACTIVE') return '#4caf50';
    if (props.status === 'WITHDRAWN') return '#f44336';
    return 'rgba(255, 255, 255, 0.2)';
  }};
  border-radius: 15px;
  padding: 20px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const ParticipantHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 15px;
`;

const ParticipantAvatar = styled.div<{ isCurrentUser: boolean }>`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${props => props.isCurrentUser 
    ? 'linear-gradient(135deg, #ffd700, #ffed4e)' 
    : 'linear-gradient(135deg, #4caf50, #8bc34a)'
  };
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.1rem;
  color: ${props => props.isCurrentUser ? '#1e293b' : 'white'};
  border: 2px solid rgba(255, 255, 255, 0.3);
`;

const ParticipantInfo = styled.div`
  flex: 1;
`;

const ParticipantName = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
  margin-bottom: 5px;
`;

const ParticipantStatus = styled.div<{ status: string }>`
  font-size: 0.8rem;
  padding: 4px 8px;
  border-radius: 10px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
    if (props.status === 'ACTIVE') return 'rgba(76, 175, 80, 0.2)';
    if (props.status === 'WITHDRAWN') return 'rgba(244, 67, 54, 0.2)';
    return 'rgba(255, 255, 255, 0.1)';
  }};
  color: ${props => {
    if (props.status === 'ACTIVE') return '#4caf50';
    if (props.status === 'WITHDRAWN') return '#f44336';
    return 'rgba(255, 255, 255, 0.8)';
  }};
  border: 1px solid ${props => {
    if (props.status === 'ACTIVE') return '#4caf50';
    if (props.status === 'WITHDRAWN') return '#f44336';
    return 'rgba(255, 255, 255, 0.2)';
  }};
`;

const ParticipantDetails = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.8);
`;

const JoinSection = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  padding: 25px;
  text-align: center;
`;

const JoinTitle = styled.h3`
  font-size: 1.3rem;
  color: #ffd700;
  margin-bottom: 15px;
`;

const JoinDescription = styled.p`
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 20px;
  line-height: 1.6;
`;

const TicketInput = styled.div`
  display: flex;
  gap: 15px;
  margin-bottom: 20px;
  justify-content: center;
  align-items: center;
`;

const TicketInputField = styled.input`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 12px 16px;
  color: white;
  font-size: 1rem;
  min-width: 300px;
  
  &:focus {
    outline: none;
    border-color: #ffd700;
    background: rgba(255, 255, 255, 0.15);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
`;

const JoinButton = styled.button`
  background: linear-gradient(45deg, #4caf50, #8bc34a);
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(76, 175, 80, 0.4);
  }
  
  &:disabled {
    background: rgba(255, 255, 255, 0.2);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const WithdrawButton = styled.button`
  background: linear-gradient(45deg, #f44336, #e91e63);
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 25px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(244, 67, 54, 0.4);
  }
`;

const StartButton = styled.button`
  background: linear-gradient(45deg, #ffd700, #ffed4e);
  color: #1e293b;
  border: none;
  padding: 18px 40px;
  border-radius: 25px;
  font-size: 1.3rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 15px;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 30px rgba(255, 215, 0, 0.4);
  }
  
  &:disabled {
    background: rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.5);
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const WithdrawalModal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1100;
`;

const WithdrawalContent = styled.div`
  background: linear-gradient(135deg, #1e293b, #334155);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: 30px;
  max-width: 500px;
  width: 90%;
`;

const WithdrawalTitle = styled.h3`
  font-size: 1.5rem;
  color: #ffd700;
  margin-bottom: 20px;
  text-align: center;
`;

const WithdrawalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormLabel = styled.label`
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
`;

const FormSelect = styled.select`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 12px 16px;
  color: white;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #ffd700;
  }
  
  option {
    background: #1e293b;
    color: white;
  }
`;

const FormTextarea = styled.textarea`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  padding: 12px 16px;
  color: white;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #ffd700;
  }
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 20px;
`;

const ModalButton = styled.button<{ variant: 'primary' | 'secondary' }>`
  background: ${props => props.variant === 'primary' 
    ? 'linear-gradient(45deg, #f44336, #e91e63)' 
    : 'rgba(255, 255, 255, 0.1)'
  };
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 12px 24px;
  border-radius: 15px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const GroupCompetitionLobby: React.FC<GroupCompetitionLobbyProps> = ({
  group,
  currentUserId,
  onClose,
  onJoinCompetition,
  onWithdraw
}) => {
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  }>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  
  const [ticketLink, setTicketLink] = useState('');
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [withdrawalReason, setWithdrawalReason] = useState<WithdrawalReasonType>('OTHER');
  const [customReason, setCustomReason] = useState('');
  const [isCurrentUserParticipant, setIsCurrentUserParticipant] = useState(false);
  const [currentUserParticipant, setCurrentUserParticipant] = useState<GroupParticipant | null>(null);

  useEffect(() => {
    // Check if current user is already a participant
    const participant = group.participants.find(p => p.userId === currentUserId);
    setIsCurrentUserParticipant(!!participant);
    setCurrentUserParticipant(participant || null);

    // Calculate time left
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const targetTime = group.status === 'ACTIVELY_FORMING' 
        ? new Date(group.formationEndTime).getTime()
        : new Date(group.competitionEndTime || 0).getTime();
      
      const difference = targetTime - now;
      
      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        
        setTimeLeft({ days, hours, minutes, seconds });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [group, currentUserId]);

  const handleJoin = () => {
    if (!ticketLink.trim()) {
      alert('Please enter your ticket link');
      return;
    }
    
    // In a real app, this would validate the ticket and add the user to the group
    console.log('Joining group with ticket:', ticketLink);
    onJoinCompetition(group.id);
  };

  const handleWithdraw = () => {
    setShowWithdrawalModal(true);
  };

  const confirmWithdrawal = () => {
    onWithdraw(group.id, withdrawalReason, customReason);
    setShowWithdrawalModal(false);
    setWithdrawalReason('OTHER');
    setCustomReason('');
  };

  const canStartCompetition = group.status === 'ACTIVELY_FORMING' && 
    group.currentParticipants >= 2 && 
    new Date() >= new Date(group.formationEndTime);

  const getStatusIcon = () => {
    switch (group.status) {
      case 'ACTIVELY_FORMING':
        return <UserPlus size={24} />;
      case 'IN_PROGRESS':
        return <Play size={24} />;
      case 'COMPLETED':
        return <Trophy size={24} />;
      default:
        return <Info size={24} />;
    }
  };

  const getStatusText = () => {
    switch (group.status) {
      case 'ACTIVELY_FORMING':
        return 'ACTIVELY FORMING - Join now before time runs out!';
      case 'IN_PROGRESS':
        return 'COMPETITION IN PROGRESS - Good luck to all participants!';
      case 'COMPLETED':
        return 'COMPETITION COMPLETED - Check results below';
      default:
        return 'Unknown Status';
    }
  };

  const getCountdownText = () => {
    if (group.status === 'ACTIVELY_FORMING') {
      return 'Time remaining to join:';
    } else if (group.status === 'IN_PROGRESS') {
      return 'Competition ends in:';
    }
    return '';
  };

  return (
    <ModalOverlay>
      <Header>
        <Title>
          <TitleIcon>🚢</TitleIcon>
          {group.name} - Lobby
        </Title>
        <CloseButton onClick={onClose}>
          <X size={18} />
          Close
        </CloseButton>
      </Header>

      <ContentArea>
        {/* Status Banner */}
        <StatusBanner status={group.status}>
          <StatusIcon>{getStatusIcon()}</StatusIcon>
          {getStatusText()}
        </StatusBanner>

        {/* Countdown Timer */}
        {(group.status === 'ACTIVELY_FORMING' || group.status === 'IN_PROGRESS') && (
          <CountdownTimer>
            <CountdownTitle>{getCountdownText()}</CountdownTitle>
            <CountdownDisplay>
              <TimeUnit>
                <TimeValue>{timeLeft.days}</TimeValue>
                <TimeLabel>Days</TimeLabel>
              </TimeUnit>
              <TimeUnit>
                <TimeValue>{timeLeft.hours}</TimeValue>
                <TimeLabel>Hours</TimeLabel>
              </TimeUnit>
              <TimeUnit>
                <TimeValue>{timeLeft.minutes}</TimeValue>
                <TimeLabel>Minutes</TimeLabel>
              </TimeUnit>
              <TimeUnit>
                <TimeValue>{timeLeft.seconds}</TimeValue>
                <TimeLabel>Seconds</TimeLabel>
              </TimeUnit>
            </CountdownDisplay>
          </CountdownTimer>
        )}

        {/* Group Information */}
        <InfoGrid>
          <InfoCard>
            <InfoTitle>
              <Users size={20} />
              Participants
            </InfoTitle>
            <InfoValue>{group.currentParticipants}</InfoValue>
            <InfoSubtext>Current members</InfoSubtext>
          </InfoCard>

          <InfoCard>
            <InfoTitle>
              <DollarSign size={20} />
              Stake Amount
            </InfoTitle>
            <InfoValue>KES {group.stakeAmount.toLocaleString()}</InfoValue>
            <InfoSubtext>Required to join</InfoSubtext>
          </InfoCard>

          <InfoCard>
            <InfoTitle>
              <Trophy size={20} />
              Prize Pool
            </InfoTitle>
            <InfoValue>KES {group.totalPrizePool.toLocaleString()}</InfoValue>
            <InfoSubtext>Total potential winnings</InfoSubtext>
          </InfoCard>

          <InfoCard>
            <InfoTitle>
              <Clock size={20} />
              Formation Period
            </InfoTitle>
            <InfoValue>
              {group.formationPeriod.months > 0 && `${group.formationPeriod.months}M `}
              {group.formationPeriod.weeks > 0 && `${group.formationPeriod.weeks}W `}
              {group.formationPeriod.days > 0 && `${group.formationPeriod.days}D `}
              {group.formationPeriod.hours > 0 && `${group.formationPeriod.hours}H `}
              {group.formationPeriod.minutes > 0 && `${group.formationPeriod.minutes}M`}
            </InfoValue>
            <InfoSubtext>Time to gather participants</InfoSubtext>
          </InfoCard>
        </InfoGrid>

        {/* Participants List */}
        <ParticipantsSection>
          <SectionTitle>
            <Users size={24} />
            Participants ({group.currentParticipants})
          </SectionTitle>
          
          <ParticipantsList>
            {group.participants.map((participant) => (
              <ParticipantCard 
                key={participant.userId}
                isCurrentUser={participant.userId === currentUserId}
                status={participant.status}
              >
                <ParticipantHeader>
                  <ParticipantAvatar isCurrentUser={participant.userId === currentUserId}>
                    {participant.userName.charAt(0).toUpperCase()}
                  </ParticipantAvatar>
                  <ParticipantInfo>
                    <ParticipantName>
                      {participant.userName}
                      {participant.userId === currentUserId && ' (You)'}
                    </ParticipantName>
                    <ParticipantStatus status={participant.status}>
                      {participant.status}
                    </ParticipantStatus>
                  </ParticipantInfo>
                </ParticipantHeader>
                
                <ParticipantDetails>
                  <span>Stake: KES {participant.stakeAmount.toLocaleString()}</span>
                  <span>Joined: {new Date(participant.joinTime).toLocaleDateString()}</span>
                </ParticipantDetails>
                
                {participant.withdrawalCount > 0 && (
                  <div style={{ 
                    marginTop: '10px', 
                    fontSize: '0.8rem', 
                    color: '#ff9800' 
                  }}>
                    Withdrawals: {participant.withdrawalCount}/{group.rules.maxWithdrawals}
                  </div>
                )}
              </ParticipantCard>
            ))}
          </ParticipantsList>
        </ParticipantsSection>

        {/* Join/Withdraw Section */}
        {group.status === 'ACTIVELY_FORMING' && (
          <JoinSection>
            {!isCurrentUserParticipant ? (
              <>
                <JoinTitle>Join This Group Competition</JoinTitle>
                <JoinDescription>
                  Submit your ticket link to join this group. Make sure you have sufficient balance 
                  to cover the stake amount of KES {group.stakeAmount.toLocaleString()}.
                </JoinDescription>
                
                <TicketInput>
                  <Link size={20} />
                  <TicketInputField
                    type="text"
                    placeholder="Paste your ticket link here..."
                    value={ticketLink}
                    onChange={(e) => setTicketLink(e.target.value)}
                  />
                </TicketInput>
                
                <ActionButtons>
                  <JoinButton onClick={handleJoin}>
                    <UserPlus size={18} />
                    Join Group
                  </JoinButton>
                </ActionButtons>
              </>
            ) : (
              <>
                <JoinTitle>You're Already a Participant!</JoinTitle>
                <JoinDescription>
                  You're successfully registered for this group competition. 
                  {canStartCompetition ? ' The competition is ready to start!' : ' Waiting for more participants...'}
                </JoinDescription>
                
                <ActionButtons>
                  <WithdrawButton onClick={handleWithdraw}>
                    <UserMinus size={18} />
                    Withdraw
                  </WithdrawButton>
                </ActionButtons>
              </>
            )}
          </JoinSection>
        )}

        {/* Start Competition Button */}
        {canStartCompetition && (
          <div style={{ textAlign: 'center' }}>
            <StartButton onClick={() => onJoinCompetition(group.id)}>
              <Play size={24} />
              Start Competition
            </StartButton>
          </div>
        )}
      </ContentArea>

      {/* Withdrawal Modal */}
      <WithdrawalModal isOpen={showWithdrawalModal}>
        <WithdrawalContent>
          <WithdrawalTitle>Withdraw from Competition</WithdrawalTitle>
          
          <WithdrawalForm>
            <FormGroup>
              <FormLabel>Reason for Withdrawal *</FormLabel>
              <FormSelect
                value={withdrawalReason}
                onChange={(e) => setWithdrawalReason(e.target.value as WithdrawalReasonType)}
              >
                <option value="POWER_BLACKOUTS_AND_OUTAGES">Power Blackouts and Outages</option>
                <option value="INTERNET_AND_DATA_CONNECTIVITY">Internet and Data Connectivity</option>
                <option value="UNEXPECTED_HEALTH_ISSUES">Unexpected Health Issues</option>
                <option value="TECHNICAL_DIFFICULTIES">Technical Difficulties</option>
                <option value="PERSONAL_EMERGENCY">Personal Emergency</option>
                <option value="OTHER">Other</option>
              </FormSelect>
            </FormGroup>

            {withdrawalReason === 'OTHER' && (
              <FormGroup>
                <FormLabel>Please specify your reason</FormLabel>
                <FormTextarea
                  placeholder="Describe your reason for withdrawal..."
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                />
              </FormGroup>
            )}

            <div style={{ 
              padding: '15px', 
              background: 'rgba(255, 152, 0, 0.1)', 
              border: '1px solid rgba(255, 152, 0, 0.3)',
              borderRadius: '10px',
              fontSize: '0.9rem',
              color: '#ff9800'
            }}>
              <strong>Warning:</strong> You have withdrawn {currentUserParticipant?.withdrawalCount || 0} times. 
              You can withdraw a maximum of {group.rules.maxWithdrawals} times. 
              After {group.rules.maxWithdrawals} withdrawals, your account will be flagged.
            </div>

            <ModalButtons>
              <ModalButton variant="secondary" onClick={() => setShowWithdrawalModal(false)}>
                Cancel
              </ModalButton>
              <ModalButton variant="primary" onClick={confirmWithdrawal}>
                Confirm Withdrawal
              </ModalButton>
            </ModalButtons>
          </WithdrawalForm>
        </WithdrawalContent>
      </WithdrawalModal>
    </ModalOverlay>
  );
};

export default GroupCompetitionLobby;
